<template>
    <form
        v-if="!user || isUsableForUser"
        data-spec-id="confirmation-form"
        class="w-full"
        @submit.prevent="onSubmit"
    >
        <div class="flex flex-col items-center rounded-sm bg-gray-700 py-12 text-white">
            <div
                v-if="!user"
                class="lg:w-2/3"
            >
                <div
                    class="mb-6 px-6"
                    data-spec-class="confirmation-form__already-a-customer"
                >
                    {{ $t('configuratorGeneral.alreadyACustomer') }}
                    <a
                        class="underline"
                        :href="`/login${redirectParams}`"
                        data-spec-class="confirmation-form__login-link"
                    >
                        {{ $t('configuratorGeneral.loginHere') }}
                    </a>
                </div>

                <div class="grid grid-cols-12 gap-x-3 gap-y-9 p-6 text-left sm:p-0">
                    <div class="col-span-12">
                        <BaseFieldInput
                            v-model="form.company"
                            :custom-theme-base="FormTheme.Website"
                            :label="t(`forms.customer.company.label`)"
                            :vuelidate-field="v$.company"
                            :hint="t(`configuratorGeneral.exclusivelyForTradespeople`)"
                            name="company"
                        />
                    </div>

                    <div class="col-span-6">
                        <BaseFieldInput
                            v-model="form.first_name"
                            :custom-theme-base="FormTheme.Website"
                            :label="t(`forms.customer.first_name.label`)"
                            :vuelidate-field="v$.first_name"
                            name="first_name"
                        />
                    </div>

                    <div class="col-span-6">
                        <BaseFieldInput
                            v-model="form.last_name"
                            :custom-theme-base="FormTheme.Website"
                            :label="t(`forms.customer.last_name.label`)"
                            :vuelidate-field="v$.last_name"
                            name="last_name"
                        />
                    </div>

                    <div class="col-span-12">
                        <BaseFieldInput
                            v-model="form.email"
                            :custom-theme-base="FormTheme.Website"
                            :label="t(`forms.customer.email.label`)"
                            :vuelidate-field="v$.email"
                            name="email"
                        />
                    </div>

                    <template v-if="collectsFullCustomerAddress">
                        <div class="col-span-12">
                            <BaseFieldSelectSearch
                                v-model="form.country_code"
                                :custom-theme-base="FormTheme.Website"
                                :options="countryStore.getCountriesAsOptions()"
                                :label="t(`${i18nCodePrefix}.country.label`)"
                                :vuelidate-field="v$.country_code"
                                name="country_code"
                            />
                        </div>

                        <div
                            v-if="form.country_code"
                            :class="{ 'col-span-8': isDach, 'col-span-12': !isDach }"
                        >
                            <BaseFieldInput
                                v-model="form.street"
                                :custom-theme-base="FormTheme.Website"
                                :label="t(`${i18nCodePrefix}.street.label`)"
                                :placeholder="t(`${i18nCodePrefix}.street.placeholder`)"
                                :vuelidate-field="v$.street"
                                name="street"
                            />
                        </div>

                        <div
                            v-if="isDach && form.country_code"
                            class="col-span-4"
                        >
                            <BaseFieldInput
                                v-model="form.number"
                                :custom-theme-base="FormTheme.Website"
                                :label="t(`${i18nCodePrefix}.number.label`)"
                                :placeholder="t(`${i18nCodePrefix}.number.placeholder`)"
                                :vuelidate-field="v$.number"
                                name="number"
                            />
                        </div>

                        <div
                            v-if="isDach && form.country_code"
                            class="col-span-4 sm:col-start-1"
                        >
                            <BaseFieldInput
                                v-model="form.zip"
                                :custom-theme-base="FormTheme.Website"
                                :label="t(`${i18nCodePrefix}.zip.label`)"
                                :placeholder="t(`${i18nCodePrefix}.zip.placeholder`)"
                                :vuelidate-field="v$.zip"
                                name="zip"
                            />
                        </div>

                        <div
                            v-if="form.country_code"
                            :class="{ 'col-span-8': isDach, 'col-span-12': !isDach }"
                        >
                            <BaseFieldInput
                                v-model="form.city"
                                :custom-theme-base="FormTheme.Website"
                                :label="t(`${i18nCodePrefix}.city.label`)"
                                :placeholder="t(`${i18nCodePrefix}.city.placeholder`)"
                                :vuelidate-field="v$.city"
                                name="city"
                            />
                        </div>

                        <div class="col-span-12">
                            <BaseFieldInput
                                v-model="form.vat_id"
                                :custom-theme-base="FormTheme.Website"
                                :label="t(`forms.customer.vat_id.label`)"
                                :vuelidate-field="v$.vat_id"
                                :is-optional="true"
                                name="vat_id"
                            />
                        </div>
                    </template>
                </div>
            </div>
            <div
                v-else
                data-spec-class="confirmation-form__logged-in-as"
            >
                <UserInfoLoggedIn
                    :user-name="user"
                    :redirect-params="redirectParams"
                />
            </div>

            <div class="mx-6 lg:w-2/3">
                <BaseAlert
                    class="mt-6 text-left"
                    type="info"
                    theme="outline"
                    :size="SizeAll.Tiny"
                >
                    <!-- eslint-disable vue/no-v-html -->
                    <p
                        v-html="
                            t(`forms.termsHint`, {
                                privacyUrl: privacyPageUrl,
                                termsUrl: parsedStatamicPageTerms.url,
                            })
                        "
                    />
                </BaseAlert>
                <!-- eslint-enable vue/no-v-html -->
            </div>

            <div class="mt-6 gap-3">
                <Confirm
                    class="flex justify-center"
                    :disabled="sendingStatus.busy || sendingStatus.success"
                >
                    <BaseSpinner
                        v-if="sendingStatus.busy || sendingStatus.success"
                        class="mr-0.5"
                    />
                    <ConfirmationOrderButton
                        :selection="selection"
                        :type="type"
                    />
                </Confirm>
            </div>

            <p
                v-if="sendingStatus.error"
                class="mt-3 text-red"
                v-text="sendingStatus.error"
            />
            <p
                v-for="message in sendingStatus.errors"
                :key="message"
                class="mt-3 text-red"
                v-text="message.join(', ')"
            />
        </div>
        <div class="honeypot">
            <input
                v-model="form.mail_address"
                type="text"
                :label="$t('honeypot')"
                name="mail_address"
                tabindex="-1"
                autocomplete="off"
            />
        </div>
    </form>
</template>

<script setup>
import { defineProps, reactive, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { currentUrlWithPathSuffix, UseFormSubmit } from '../functions/useFormSubmit';
import Confirm from './Configurator/Confirm.vue';
import { FormTheme, SizeAll } from '@/types';
import { required, email } from '../../your/i18n/i18n-validators';
import useVuelidate from '@vuelidate/core';
import { useAddressSetup } from '../../your/composables/address';
import { useCountryStore } from '../../your/stores/countryStore';
import UserInfoLoggedIn from './UserInfoLoggedIn.vue';
import ConfirmationOrderButton from './ConfirmationOrderButton.vue';
import { AssortmentType } from '../../../types';

const { t } = useI18n();
const countryStore = useCountryStore();

const props = defineProps({
    type: { type: String, required: true },
    price: { type: Number, required: true },
    setupPrice: { type: Number, default: null },
    configuration: { type: [Object, String], default: null },
    selection: { type: Object, default: null },
    urlModifier: { type: String, default: '' },
    redirectParams: { type: String, default: '' },
    orderHint: { type: String, default: '' },
    isUsableForUser: { type: Boolean, default: false },
});

const privacyPageUrl = document
    .querySelector('meta[name="privacy-page-url"]')
    .getAttribute('content');

const statamicPageTerms = document
    .querySelector('meta[name="statamic-page-terms"]')
    .getAttribute('content');
const parsedStatamicPageTerms = JSON.parse(statamicPageTerms);

const collectsFullCustomerAddress = computed(
    () => props.type === AssortmentType.LatestServer || props.type === AssortmentType.ServerDeals
);

const {
    fields: addressFields,
    rules: addressRules,
    isDach,
    i18nCodePrefix,
} = useAddressSetup(undefined);

const rules = computed(() => ({
    ...(collectsFullCustomerAddress.value ? addressRules.value : {}),
    first_name: { required },
    last_name: { required },
    email: { required, email },
    company: { required },
}));

const submitData = reactive({
    configuration: props.configuration,
    type: props.type,
    price: props.price,
    setupPrice: props.setupPrice,
    selection: props.selection,
    lang: document.documentElement.lang,
    ...addressFields,
    first_name: '',
    last_name: '',
    email: '',
    company: '',
    vat_id: '',
    mail_address: '',
    is_dach: false,
});

const { form, send, sendingStatus } = UseFormSubmit(
    currentUrlWithPathSuffix(props.urlModifier),
    submitData
);

const user = document.querySelector('meta[name="user"]').getAttribute('content') || '';

const v$ = useVuelidate(user ? computed(() => ({})) : rules, form);

onMounted(() => {
    if (!user && collectsFullCustomerAddress.value) {
        countryStore.fetchCountries();
    }
});

const onSubmit = async () => {
    v$.value.$touch();
    if (v$.value.$error) {
        return;
    }

    try {
        await send(false);
        if (sendingStatus.success) {
            const locale = document.documentElement.lang;
            window.location.href = `/${locale}/thank-you?type=${props.type}&price=${props.price}`;
        }
    } catch {
        /* intentionally empty */
    }
};

watch(
    () => form.country_code,
    () => {
        if (!collectsFullCustomerAddress.value) {
            return;
        }
        addressFields.country_code = form.country_code;
        form.is_dach = isDach.value;
    }
);
</script>
